import { inject, Injectable } from '@angular/core';
import { AddPaymentbycardRequest } from '@api/src/models/AgentApi/Contract/add-paymentbycard-request';
import { AddPaymentbycardResponse } from '@api/src/models/AgentApi/Contract/add-paymentbycard-response';
import { AddPtTicketRequest } from '@api/src/models/AgentApi/Contract/add-pt-ticket-request';
import { AddPtTicketResponse } from '@api/src/models/AgentApi/Contract/add-pt-ticket-response';
import { AddPurchaseRequest } from '@api/src/models/AgentApi/Contract/add-purchase-request';
import { AddPurchaseResponse } from '@api/src/models/AgentApi/Contract/add-purchase-response';
import { ClientIdentificationRequest } from '@api/src/models/AgentApi/Contract/client-identification-request';
import { ClientIdentificationResponse } from '@api/src/models/AgentApi/Contract/client-identification-response';
import { GetClientBalanceRequest } from '@api/src/models/AgentApi/Contract/get-client-balance-request';
import { GetClientBalanceResponse } from '@api/src/models/AgentApi/Contract/get-client-balance-response';
import { ListPtTariffResponse } from '@api/src/models/AgentApi/Contract/list-pt-tariff-response';
import { ListTicketTariffResponse } from '@api/src/models/AgentApi/Contract/list-ticket-tariff-response';
import { RegisterCardRequest } from '@api/src/models/AgentApi/Contract/register-card-request';
import { RegisterCardResponse } from '@api/src/models/AgentApi/Contract/register-card-response';
import { SellCardRequest } from '@api/src/models/AgentApi/Contract/sell-card-request';
import { SellCardResponse } from '@api/src/models/AgentApi/Contract/sell-card-response';
import { AgentService } from '@api/src/services/agent.service';
import { ErrorResponse } from '@core/error/error-response.model';
import { startWithErrors } from '@core/operators/start-with-errors';
import { startWithLoading } from '@core/operators/start-with-loading';
import { StartWithUnit } from '@core/operators/start-with-unit';
import { RxState, rxState } from '@rx-angular/state';
import { rxActions } from '@rx-angular/state/actions';
import { MatDialogService } from '@shared/services/mat-dialog.service';
import { filter, Observable, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AddCardForm } from '@shared/components/add-card-form/add-card-form';

export type IClientState = {
    loading: Record<StartWithUnit, boolean>;
    errors: Record<StartWithUnit, ErrorResponse>;
    client: ClientIdentificationResponse;
    clientBalance: GetClientBalanceResponse;
    ticketTariffs: ListTicketTariffResponse[];
    ticketPtTariffs: ListPtTariffResponse[];
};

export type IClientActions = {
    requestSearchClient: ClientIdentificationResponse;
    requestClientBalance: GetClientBalanceResponse;
    requestTicketTariffs: ListTicketTariffResponse[];
    requestTicketPtTariffs: ListPtTariffResponse[];
};

@Injectable({
    providedIn: 'root',
})
export class ClientState extends RxState<IClientState> {
    readonly #agentService: AgentService = inject(AgentService);
    readonly #actions = rxActions<IClientActions>();
    readonly #state = rxState<IClientState>();
    readonly #dialog: MatDialogService = inject(MatDialogService);

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.#state.select('loading');
    public readonly errors$: Observable<Record<StartWithUnit, ErrorResponse>> = this.#state.select('errors');
    public readonly client$: Observable<ClientIdentificationResponse> = this.#state.select('client');
    public readonly clientBalance$: Observable<GetClientBalanceResponse> = this.#state.select('clientBalance');

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();
    }

    private setDefaultState(): void {
        this.#state.set({
            client: null,
            loading: null,
            errors: null,
        });
    }

    private connectSelectors(): void {
        this.#state.connect('ticketTariffs', this.#actions.requestTicketTariffs$);
        this.#state.connect('ticketPtTariffs', this.#actions.requestTicketPtTariffs$);
        this.#state.connect('clientBalance', this.#actions.requestClientBalance$);
        this.#state.connect(
            'client',
            this.#actions.requestSearchClient$.pipe(
                tap(() => {
                    this.#state.set({ errors: null });
                    this.#dialog.close(AddCardForm.name);
                }),
            ),
        );

        this.#state.connect(
            'client',
            this.#actions.requestSearchClient$.pipe(
                filter(Boolean),
                tap((client: ClientIdentificationResponse) => {
                    this.getClientBalance({
                        mobnum: client.mobilePhone,
                    });
                }),
            ),
        );
    }

    public resetErrors(): void {
        this.#state.set({ errors: null });
    }

    public searchClient(body: ClientIdentificationRequest): void {
        this.hold(
            this.#agentService
                .apiAgentClientIdentificationPost({ body })
                .pipe(
                    startWithLoading(this.#state, StartWithUnit.ClientSearch),
                    startWithErrors(this.#state, StartWithUnit.ClientSearch),
                ),
            this.#actions.requestSearchClient,
        );
    }

    public getClientBalance(body: GetClientBalanceRequest): void {
        this.hold(this.#agentService.apiAgentGetClientBalancePost({ body }), this.#actions.requestClientBalance);
    }

    public resetClient(): void {
        this.#actions.requestSearchClient(null);
    }

    public addPtTicket(body: AddPtTicketRequest): Observable<AddPtTicketResponse | AddPtTicketResponse[]> {
        return this.#agentService
            .apiAgentAddPtTicketPost({ body })
            .pipe(
                startWithLoading(this.#state, StartWithUnit.AddPtTicket),
                startWithErrors(this.#state, StartWithUnit.AddPtTicket),
            );
    }

    public topUp(body: AddPaymentbycardRequest): Observable<AddPaymentbycardResponse> {
        return this.#agentService
            .apiAgentAddPaymentbycardPost({ body })
            .pipe(
                startWithLoading(this.#state, StartWithUnit.TopUpByToken),
                startWithErrors(this.#state, StartWithUnit.TopUpByToken),
            );
    }

    public buyTicketOnMedium(body: AddPurchaseRequest): Observable<AddPurchaseResponse> {
        return this.#agentService
            .apiAgentAddPurchasePost({ body })
            .pipe(
                startWithLoading(this.#state, StartWithUnit.BuyTicket),
                startWithErrors(this.#state, StartWithUnit.BuyTicket),
            );
    }

    public sellCard(body: SellCardRequest): Observable<SellCardResponse> {
        return this.#agentService
            .apiAgentCheckIdIsdoublePost({
                body: {
                    mediumType: body.mediumType,
                    mediumN: body.mediumN,
                },
            })
            .pipe(
                switchMap(() => this.#agentService.apiAgentSellCardPost({ body })),
                startWithLoading(this.#state, StartWithUnit.SellOrRegisterCard),
                startWithErrors(this.#state, StartWithUnit.SellOrRegisterCard),
            );
    }

    public registryCard(body: RegisterCardRequest): Observable<RegisterCardResponse> {
        return this.#agentService
            .apiAgentCheckIdIsdoublePost({
                body: {
                    mediumType: body.mediumType,
                    mediumN: body.mediumN,
                },
            })
            .pipe(
                switchMap(() => this.#agentService.apiAgentRegisterCardPost({ body })),
                startWithLoading(this.#state, StartWithUnit.SellOrRegisterCard),
                startWithErrors(this.#state, StartWithUnit.SellOrRegisterCard),
            );
    }
}
